import ProductConfigurationContext from '@jetshop/core/components/ProductConfigurationProvider/ProductConfigurationContext';
import { useStockStatus } from '@jetshop/ui/Stock/StockStatusContainer';
import { theme } from '../Theming/Theming';
import React, { useContext, useState } from 'react';
import { styled } from 'linaria/react';
import { GridPrice as Price } from '../Price';
import { Price as UIPrice } from '@jetshop/ui/Price';
import { useDynamicPrice } from '@jetshop/core/hooks/useDynamicPrice';
import NotifyWhenBack from './StockStatus/NotifyWhenBack';
import KlaviyoBackInStock from './StockStatus/KlaviyoBackInStock';
import StockStatusIndicator from './StockStatus/StockStatusIndicator';
import { VariantSelector } from './VariantSelector/VariantSelector';
import AddToCartForm from './AddToCart/AddToCartForm';
import { Form } from 'formik';
import AddToCartWrapper, { buyHeightStyle } from './AddToCart/AddToCartWrapper';
import { ProductInfoMarginBottom } from './StyledComponents';
import { useProductValidationMessage } from './helpers';
import loadable from '@loadable/component';
import Spinner from '../ui/Spinner';
import ProductUspBox from './ProductUspBox';
import { Favourite } from '../ProductList/Favourite';
import { AddCommentProduct } from './PrintProducts/AddCommentProduct';
import { Query } from 'react-apollo';
import PrintQuery from './PrintProducts/PrintQuery.gql';
import t from '@jetshop/intl';
import { getFontSize } from '../Theming/helpers';
import { ProductDiscount } from '../CategoryPage/ProductDiscount';
import CommentInput from './PrintProducts/CommentInput';
import SiteContentContext from '../SiteContent/SiteContentContext';
import Popup from '../ui/Popup';
import InfoIcon from '../ui/icons/InfoIcon';
import UpsellProducts from './UpsellProduct/UpsellProducts';
import RightIcon from '../ui/icons/RightIcon';
import useBuyButtons from '../ui/hooks/useBuyButtons';
import ContentPopup from '../ui/ContentPopup';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import LipscoreWidget from '../Lipscore/LipscoreWidget';
import { getKlaviyoCompanyId, getKlaviyoListId } from '../Klaviyo';
import SiblingProducts from './SiblingProducts/SiblingProducts';
import { PriceHistoryWrapper } from './Price/PriceHistory';
import Tooltip from '../ui/ToolTip';

const LoadablePackageProduct = loadable(
  () => import('./PackageProduct/PackageProduct'),
  {
    fallback: <Spinner />
  }
);

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  ${theme.above.md} {
    align-items: flex-start;
  }
  .visible {
  }
  .hide {
    display: none;
  }
  .german-vat-popup {
    button {
      text-decoration: underline;
    }
  }
`;

const Header = styled('header')`
  width: 100%;
  ${ProductInfoMarginBottom};
  .lipscore-ratings-wrapper {
    margin-top: 1rem;
  }
`;

const Heading = styled('h2')`
  display: flex;
  justify-content: space-between;
  font-family: ${theme.productPage.name.fontFamily} !important;
  font-weight: ${theme.productPage.name.fontWeight} !important;
`;

const SubHeading = styled('h3')``;

const ShortDescription = styled('article')`
  line-height: ${theme.typography.paragraph.lineHeight};
  ${theme.below.md} {
    font-size: 0.87rem;
    display: none;
  }
  ${ProductInfoMarginBottom}
`;

const ArticleNumber = styled('div')`
  ${ProductInfoMarginBottom}
  .article-number-label {
    font-weight: ${theme.weights.normal};
  }
  .article-number-value {
    margin-left: 5px;
  }
`;

const FlexRow = styled('div')`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  ${ProductInfoMarginBottom}
  [data-flight-price] {
    ${UIPrice.Normal} {
      ${getFontSize(theme.productPage.price.normal.fontSize)};
      font-weight: ${theme.productPage.price.fontWeight};
    }
    ${UIPrice.New} {
      ${getFontSize(theme.productPage.price.new.fontSize)};
    }
    ${UIPrice.Old} {
      ${getFontSize(theme.productPage.price.old.fontSize)};
    }
  }

  .product-discount-inner-wrapper {
    [data-flight-price] .price,
    .text {
      font-size: 1rem !important;
    }
  }
`;

const Col = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  &:last-child {
    align-items: flex-end;
  }
  .price-container {
    display: flex;
    align-items: center;
    gap: 8px;
    .tooltip-wrapper {
      display: flex;
      .price-history-wrapper {
        width: 100%;
      }
    }
  }
`;

const StockStatusWrapper = styled('div')`
  .stock-wrapper {
    margin-bottom: 0;
  }
  .stock-label {
    font-weight: ${theme.weights.normal};
    text-transform: none;
  }
  .free-freight-stock {
    display: inline-block;
    margin: 0 0 0 0.8rem;
  }
`;

const VariantsWrapper = styled('div')`
  width: 100%;
  > div {
    ${ProductInfoMarginBottom}
  }
`;

const StyledForm = styled(Form)`
  display: flex;
  width: 100%;
  flex-direction: column;
  margin-top: 0.5rem;
  .comment {
    display: flex;
    &.multiple {
      .comment-header {
        cursor: pointer;
      }
    }
    .comment-info {
      width: 100%;
    }
    .comment-radio {
      margin-right: 1rem;
      margin-bottom: 1rem;
      input {
        width: 20px;
        height: 20px;
        cursor: pointer;
      }
    }
  }
`;

const InputHeaderWrapper = styled('div')`
  > span {
    font-size: 1rem;
    &:first-child {
      font-weight: ${theme.weights.bold};
    }
  }
  .desc {
    margin-left: 10px;
    font-style: italic;
  }
  div {
    display: inline-block !important;
  }
  > div {
    margin-left: 10px;
    &:before {
      content: '(';
      margin-right: 3px;
    }
    &:after {
      content: ')';
      margin-left: 3px;
    }
    > div > div {
      font-size: 1rem !important;
    }
  }
`;

const ToggleButton = styled('button')`
  border: 1px solid #ddd;
  &:not(.down) {
    background: #f1f1f1;
  }
  width: 100%;
  ${buyHeightStyle}
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  margin: 1rem 0;
  > svg {
    color: ${theme.colors.buy};
  }
  &.down {
    padding: 0;
    border-bottom: none;
    border-left: none;
    border-right: none;
    > svg {
      transform: rotate(90deg);
    }
  }
`;

const ProductDetailsHeaderWrapper = styled.h4`
  margin: 1rem 0;
  > svg {
    margin-left: 5px;
    cursor: pointer;
  }
  &.upsell-combo {
    &:before {
      content: '1. ';
    }
  }
`;

const CommentsWrapper = styled.div`
  margin-bottom: 1rem;
  .comments-text {
    margin-bottom: 1rem;
    a,
    a:active {
      color: ${theme.colors.primary};
    }
  }
  &.upsell-combo {
    h4:before {
      content: '2. ';
    }
    .comments-list {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      > div {
        width: 100%;
        &:nth-of-type(-n + 2) {
          width: 49%;
        }
      }
    }
  }
`;

const ProductDetailsHeader = ({
  className = '',
  title,
  content,
  onClick = () => {}
}) => {
  const [popup, setPopup] = useState(false);
  return (
    <ProductDetailsHeaderWrapper className={className}>
      <span onClick={onClick}>{title}</span>
      <InfoIcon
        onClick={() => {
          setPopup(true);
        }}
      />
      {popup && (
        <Popup isOpen={popup} closePopup={() => setPopup(false)}>
          {content}
        </Popup>
      )}
    </ProductDetailsHeaderWrapper>
  );
};

const Comments = ({ product }) => {
  const isVertical = product.customerComments.filter(
    item => item.name.search('vertical') !== -1
  )[0];
  const [inputIndex, setInputIndex] = useState(0);
  return (
    <div className="comments-list">
      {product.customerComments &&
        product.customerComments.map((comment, index) => (
          <Query
            query={PrintQuery}
            variables={{
              articleNumber: comment.name
            }}
            key={index}
          >
            {({ data }) => {
              const product = data?.product;
              const customLabel = product?.articleNumber === comment.name;
              const label = !customLabel ? comment.name : ' ';
              return (
                <div className={`comment ${isVertical ? 'multiple' : ''}`}>
                  {isVertical && (
                    <div className="comment-radio">
                      <input
                        type="radio"
                        name="vertical-comment"
                        checked={inputIndex === index}
                        onChange={() => setInputIndex(index)}
                      />
                    </div>
                  )}
                  <div className="comment-info">
                    {customLabel && (
                      <InputHeaderWrapper
                        className="comment-header"
                        onClick={() => setInputIndex(index)}
                      >
                        <span>{product.name}</span>
                        <Price {...product} />
                        <span className="desc">{product.shortDescription}</span>
                      </InputHeaderWrapper>
                    )}
                    <CommentInput
                      wrapperClassName="customer-comment-input-wrapper"
                      className="customer-comment-input"
                      data-testid={`${comment.name}-input`}
                      id={`comments['${comment.name}']`}
                      name={`comments['${comment.name}']`}
                      label={label}
                      key={comment.name}
                      maxLength={45}
                      data-comment-name={comment.name}
                      data-print-comment={customLabel}
                      disabled={isVertical && inputIndex !== index}
                      {...(isVertical && inputIndex !== index
                        ? { newValue: '' }
                        : {})}
                    />
                  </div>
                </div>
              );
            }}
          </Query>
        ))}
    </div>
  );
};

const Details = ({ product, variantHandler, customData }) => {
  const {
    selectedVariant: selectedVariation,
    validation: variantValidation,
    getMissingOptions,
    missingOptions
  } = variantHandler;

  const selected = selectedVariation || product;
  const configurationContext = useContext(ProductConfigurationContext);
  const stockStatus = useStockStatus(selectedVariation || product);
  const price = useDynamicPrice(product, selectedVariation);
  const useBuyButtonsVar = useBuyButtons();
  const outOfStockNotify = stockStatus.status === 'notifyWhenBack';
  const { selectedChannel } = useContext(ChannelContext);
  // Shops with a Klaviyo company_id subscribe through Klaviyo instead of
  // Jetshop's own stock notifications. Jetshop only reports 'notifyWhenBack'
  // when stock notifications are enabled on the article, which no shop uses,
  // so Klaviyo is offered on plain 'outOfStock' articles as well.
  const klaviyoCompanyId = getKlaviyoCompanyId(selectedChannel);
  // Only channels with a list configured get the newsletter opt-in.
  const klaviyoListId = getKlaviyoListId(selectedChannel);
  const showKlaviyoBackInStock =
    !!klaviyoCompanyId &&
    (outOfStockNotify || stockStatus.status === 'outOfStock');

  const { hasVariants, hasUpsell, hidePrice } = product;
  const { validationMessage, enableValidation } = useProductValidationMessage({
    missingOptions,
    variantValidation,
    hasVariants,
    stockStatus
  });

  const {
    upsellComboHeaderSkiis,
    upsellHeaderSkiis,
    upsellHeaderPopupSkiis,
    upsellHeader,
    commentsHeaderSkiis,
    commentsHeaderPopupSkiis,
    commentsText
  } = useContext(SiteContentContext);

  const completeProduct = product.stockStatus?.text;
  const hasComments =
    product.customerComments && product.customerComments.length > 0;
  const upsellCombo = hasUpsell && hasComments;

  const [fetchCombo, setFetchCombo] = useState(false);
  const [showCombo, setCombo] = useState(false);
  const [addon, setAddon] = useState(false);

  const germanVatPopup =
    theme.config.settings.germanVatContentId && selectedChannel.name === 'DE';

  return (
    <Wrapper>
      <Header>
        <Heading data-testid="product-title">
          {product.name}
          <Favourite product={product} />
        </Heading>
        {product.subName && <SubHeading>{product.subName}</SubHeading>}
        <div className="lipscore-ratings-wrapper">
          <LipscoreWidget
            type="rating"
            product={product}
            selectedVariant={selectedVariation}
          />
        </div>
      </Header>
      <ArticleNumber className="article-number-wrapper">
        <span className="article-number-label">{t('Article number:')}</span>
        <span className="article-number-value">{selected.articleNumber}</span>
      </ArticleNumber>
      {product.shortDescription && (
        <ShortDescription
          dangerouslySetInnerHTML={{
            __html: product.shortDescription
          }}
        />
      )}
      <SiblingProducts product={product} customData={customData} />
      {product?.isPackage ? (
        <LoadablePackageProduct product={product} />
      ) : (
        <AddToCartForm
          product={product}
          variant={selectedVariation}
          getMissingOptions={getMissingOptions}
        >
          {(addedToCart, setAdded) => {
            return (
              <>
                <FlexRow>
                  <Col>
                    <div className="price-container">
                      <Price
                        {...price}
                        defaultPrice={product.defaultPrice}
                        css={{
                          opacity:
                            configurationContext && configurationContext.loading
                              ? 0.3
                              : 1
                        }}
                      />
                      <Tooltip
                        trigger={<InfoIcon />}
                        triggerLabel={t('Price info')}
                        placement="bottom"
                        className="tooltip-wrapper"
                      >
                        <PriceHistoryWrapper
                          articleNumber={product.articleNumber}
                          variant={selectedVariation}
                        />
                      </Tooltip>
                    </div>
                    {germanVatPopup && (
                      <p className="german-vat-popup">
                        inkl. MwSt. zzgl.{' '}
                        <ContentPopup
                          pageId={theme.config.settings.germanVatContentId}
                          title="Versandkosten"
                        />
                      </p>
                    )}
                  </Col>
                  <Col>
                    <StockStatusWrapper>
                      <StockStatusIndicator
                        status={stockStatus.status}
                        text={stockStatus.text}
                      />
                    </StockStatusWrapper>
                  </Col>
                  {theme.productGrid.product.discount && (
                    <ProductDiscount product={product} />
                  )}
                </FlexRow>
                {product.hasVariants && (
                  <VariantsWrapper>
                    <VariantSelector
                      product={product}
                      variantHandler={variantHandler}
                      showValidation={!!validationMessage}
                    />
                  </VariantsWrapper>
                )}
                {upsellCombo && (
                  <ToggleButton
                    onClick={
                      !showCombo
                        ? () => {
                            setFetchCombo(true);
                            setCombo(true);
                          }
                        : () => {}
                    }
                    className={`${showCombo ? 'down' : ''}`}
                  >
                    {!showCombo ? (
                      <h5>{upsellComboHeaderSkiis}</h5>
                    ) : (
                      <ProductDetailsHeader
                        title={upsellHeaderSkiis}
                        content={upsellHeaderPopupSkiis}
                        onClick={() => setCombo(!showCombo)}
                        className="upsell-combo"
                      />
                    )}
                    <RightIcon
                      onClick={() => {
                        setFetchCombo(true);
                        setCombo(!showCombo);
                      }}
                    />
                  </ToggleButton>
                )}
                {hasUpsell && (fetchCombo || !upsellCombo) && (
                  <div
                    className={showCombo || !upsellCombo ? 'visible' : 'hide'}
                    style={{ width: '100%' }}
                  >
                    {!upsellCombo && (
                      <ProductDetailsHeaderWrapper>
                        {upsellHeader}
                      </ProductDetailsHeaderWrapper>
                    )}
                    <UpsellProducts
                      product={product}
                      addToCartTrigger={addedToCart}
                      setAddon={setAddon}
                      addon={addon}
                    />
                  </div>
                )}
                <StyledForm>
                  {hasComments &&
                    ((fetchCombo && addon) || !upsellCombo) &&
                    useBuyButtonsVar && (
                      <CommentsWrapper
                        className={`${
                          showCombo || !upsellCombo ? 'visible' : 'hide'
                        } ${upsellCombo ? 'upsell-combo' : ''}`}
                      >
                        {upsellCombo && (
                          <ProductDetailsHeader
                            title={commentsHeaderSkiis}
                            content={commentsHeaderPopupSkiis}
                          />
                        )}
                        {upsellCombo && commentsText && (
                          <div className="comments-text">{commentsText}</div>
                        )}
                        <Comments product={product} />
                      </CommentsWrapper>
                    )}
                  {!showKlaviyoBackInStock &&
                    !outOfStockNotify &&
                    completeProduct &&
                    !hidePrice && (
                      <>
                        <AddCommentProduct
                          addedToCart={addedToCart}
                          setAdded={setAdded}
                          product={product}
                          selected={selected}
                        />
                        <AddToCartWrapper
                          validationMessage={validationMessage}
                          enableValidation={enableValidation}
                        />
                      </>
                    )}
                </StyledForm>
                {showKlaviyoBackInStock ? (
                  <KlaviyoBackInStock
                    articleNumber={selected.articleNumber}
                    companyId={klaviyoCompanyId}
                    listId={klaviyoListId}
                  />
                ) : (
                  outOfStockNotify && (
                    <NotifyWhenBack articleNumber={selected.articleNumber} />
                  )
                )}
              </>
            );
          }}
        </AddToCartForm>
      )}
      {useBuyButtonsVar && <ProductUspBox />}
    </Wrapper>
  );
};

export default Details;
